import { useState, useEffect } from "react";
import { Sparkles, Megaphone, Smile, BriefcaseBusiness, ArrowDownWideNarrow, ArrowUpNarrowWide, Images } from "lucide-react";
import { ImageGenerator } from "@/components/inspiration/ImageGenerator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePostHog } from 'posthog-js/react';
import { useUser } from '@supabase/auth-helpers-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface EditorImageSectionProps {
  content: string;
  onImagesGenerated: (images: string[]) => void;
  onImageSelect: (selectedImages: string[]) => void;
  onContentUpdate?: (newContent: string) => void;
}

export const EditorImageSection = ({
  content,
  onImagesGenerated,
  onImageSelect,
  onContentUpdate,
}: EditorImageSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const posthog = usePostHog();
  const [user, setUser] = useState<any>(null);
  const showAiWritingOptions = posthog?.isFeatureEnabled('show-ai-writing-options');

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user?.email) return;

      try {
        const { data, error } = await supabase.functions.invoke('check-customer-subscription', {
          body: { email: user.email },
        });

        if (error) throw error;
        setIsSubscribed(data.isSubscribed);
      } catch (error) {
        console.error('Error checking subscription:', error);
        setIsSubscribed(false);
      }
    };

    checkSubscription();
  }, [user?.email]);

  const shouldShowAiWritingOptions = showAiWritingOptions && isSubscribed;

  const handleWritingOperation = async (operation: string) => {
    if (!content.trim()) {
      toast.error("Please add some content first");
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading("Updating content...");

    try {
      const { data, error } = await supabase.functions.invoke('ai-writing-function', {
        body: { 
          content: content,
          operation: operation
        },
      });

      if (error) throw error;

      onContentUpdate?.(data.generatedText);
      toast.dismiss(toastId);
      toast.success("Content updated successfully!");
    } catch (error) {
      console.error('Error updating content:', error);
      toast.dismiss(toastId);
      toast.error("Failed to update content");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isLoading}>
          <button
            className="absolute -top-[72px] right-4 p-3 rounded-full 
            bg-white hover:bg-primary/5 
            border-2 border-primary/50 hover:border-primary
            shadow-lg hover:shadow-xl hover:shadow-primary/20
            transition-all duration-300 
            hover:scale-105
            group
            disabled:opacity-50"
          >
            <Sparkles className="h-5 w-5 text-primary transition-transform duration-300 group-hover:rotate-12" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[300px] p-2">
          {shouldShowAiWritingOptions && (
            <div className="mb-2">
              <div className="text-sm font-medium text-muted-foreground px-2 mb-1">Writing</div>
              <DropdownMenuItem 
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => handleWritingOperation('shorter')}
                disabled={isLoading}
              >
                <ArrowDownWideNarrow className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Make it shorter</span>
                  <span className="text-xs text-muted-foreground">Condense your post</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => handleWritingOperation('longer')}
                disabled={isLoading}
              >
                <ArrowUpNarrowWide className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Make it longer</span>
                  <span className="text-xs text-muted-foreground">Expand your post</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => handleWritingOperation('professional')}
                disabled={isLoading}
              >
                <BriefcaseBusiness className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Make it professional</span>
                  <span className="text-xs text-muted-foreground">Polish your writing</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => handleWritingOperation('funny')}
                disabled={isLoading}
              >
                <Smile className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Make it funny</span>
                  <span className="text-xs text-muted-foreground">Add humor to your post</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2 p-2 cursor-pointer"
                onClick={() => handleWritingOperation('viral')}
                disabled={isLoading}
              >
                <Megaphone className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Make it viral</span>
                  <span className="text-xs text-muted-foreground">Optimize for engagement</span>
                </div>
              </DropdownMenuItem>
            </div>
          )}

          <div>
            <div className="text-sm font-medium text-muted-foreground px-2 mb-1">Images</div>
            <DropdownMenuItem
              onClick={() => {
                const generator = document.querySelector('[data-generate-button] button');
                if (generator instanceof HTMLButtonElement) {
                  generator.click();
                }
              }}
              className="flex items-center gap-2 p-2 cursor-pointer"
            >
              <Images className="h-4 w-4" />
              <div className="flex flex-col">
                <span className="font-medium">Generate images</span>
                <span className="text-xs text-muted-foreground">Create AI images for your post</span>
              </div>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <div>
        <ImageGenerator 
          content={content} 
          onImagesGenerated={onImagesGenerated}
          onImageSelect={onImageSelect}
        />
      </div>
    </div>
  );
};